import React, { useCallback, useMemo, useState } from 'react';
import { MailOutlined, PhoneOutlined } from '@ant-design/icons';
import { i18n } from '@guuru/translation-web';
import { Space } from 'antd';
import { FormItem } from '../../../SearchInput';
import StatusIcon from './StatusIcon';
import ContactTabs from './ContactTabs';
import TextInput from '../../../TextInput';

interface ContactsProps {
  contactType: string;
  saving: boolean;
  contacts?: {
    email?: string;
    phone?: string;
  },
  readOnly?: boolean;
  submited?: boolean;
  email?: string;
  phone?: string;
  contactTab?: string;
  // eslint-disable-next-line no-unused-vars
  setContactTab?: (_: string) => void;
}

const Contacts = function ({
  contactType,
  saving,
  contacts = {},
  readOnly = false,
  submited = false,
  email = undefined,
  phone = undefined,
  contactTab = undefined,
  setContactTab = () => {},
}: ContactsProps) {
  const [customFields, setCustomFields] = useState<Record<string, boolean>>({});

  const handleContactTypeChange = (activeKey: string) => {
    setContactTab(activeKey);
  };

  const onChange = useCallback((inputName: string) => {
    setCustomFields({ ...customFields, [inputName]: true });
  }, [customFields]);

  const contact = useCallback((
    name: string,
    icon: React.ComponentProps<typeof StatusIcon>['icon'],
    confirmed: boolean,
    type: 'string' | 'email' = 'string',
  ) => {
    const label = name.charAt(0).toUpperCase() + name.slice(1);
    const text = i18n.t(`bot:::messageTypeContactMessage${label}`);
    const validation = i18n.t(`bot:::messageTypeContactValidation${label}`);
    const placeholder = i18n.t(`bot:::messageTypeContactPlaceholder${label}`);
    const fieldTouched = customFields[name];
    const required = true;

    return {
      key: name,
      forceRender: true,
      destroyOnHidden: false,
      label: (
        <Space>
          <StatusIcon
            isConfirmed={confirmed}
            isTouched={fieldTouched}
            icon={icon}
          />
          {text}
        </Space>
      ),
      children: (
        <FormItem
          help={i18n.t('bot:::inputContactHelpMessage')}
          name={name}
          rules={[
            {
              required,
              message: validation,
            }, {
              type,
              message: validation,
            },
          ]}
        >
          <TextInput
            placeholder={placeholder}
            disabled={saving || readOnly}
            onChange={() => onChange(name)}
          />
        </FormItem>
      ),
    };
  }, [
    customFields,
    onChange,
    readOnly,
    saving,
  ]);

  const items = useMemo(() => {
    const tabs = [];

    if (['email', 'any'].includes(contactType)) {
      const isValid = submited && !!(email || (!email && !!contacts?.email));
      tabs.push(contact('email', MailOutlined, isValid, 'email'));
    }

    if (['phone', 'any'].includes(contactType)) {
      const isValid = submited && !!(phone || (!phone && !!contacts?.phone));
      tabs.push(contact('phone', PhoneOutlined, isValid, 'string'));
    }

    return tabs;
  }, [
    contact,
    contactType,
    contacts,
    email,
    phone,
    submited,
  ]);

  return (
    <ContactTabs
      destroyOnHidden={false}
      activeKey={contactTab}
      defaultActiveKey={contactTab}
      onChange={handleContactTypeChange}
      tabBarGutter={14}
      items={items}
    />
  );
};

export default Contacts;
